import { Form, Formik } from "formik";
import React, { useContext, useRef } from "react";
import { FormContext } from "../../../App";
import * as yup from "yup";
import { Container, Paper, useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import Divider from "@mui/material/Divider";
import { styled } from "@mui/system";
import ProgressBar from "./ProgressBar";
import TermAndCondition from "components/atoms/TermAndCondition";
import ZipCodeInput from "components/atoms/ZipcodeInput";
import EmailInput from "components/atoms/EmailInput";
import TextInput from "components/atoms/TextInput";
import "../MultiStep-Estimate/style.scss";
import DataLayerService from "utility/dataLayerService";
import PostService from "utility/postService";
import Validation, { useZipCodeValidation } from "utility/validation";
import LeafBox from "components/atoms/LeafBox";
import { LeafButton } from "components/atoms/LeafButtons";
import { useProduct, useComponentName } from "hooks";
import { scrollToTop, modalScrollToTop } from '../../../utility/functions';

const Step1 = (props) => {
  const location = props.dataSet?.location ? props.dataSet.location : '';
  const form = props.dataSet?.form ? props.dataSet.form : '';
  const zipSource = props.dataSet?.zipsource || "localzip";
  let zipCode = '';
  let fullName = '';
  let emailAddress = '';
  let emailOptIn = false;
  let zipcodeInputValue = '';
  let serviceAreaFlag = false;
  const product = useProduct();
  const userJourneyTracking = props.dataSet?.tracking || '';
  const componentName = props.component || 'ZipChecker';
  const formName = useComponentName(componentName);
  const subformName = useComponentName(props.modalComponent);
  let zipCodeValidationStatus = 'invalid';
  const formRef = useRef(null);

  // Zipcode Validation based on the Country Code.
  const zipCodeValidation = useZipCodeValidation();

  const { activeStepIndex, setActiveStepIndex, formData, setFormData } =
    useContext(FormContext);

  const ValidationSchema = yup.object().shape({
    zipCode: yup
      .string()
      .matches(zipCodeValidation.validationPattern, zipCodeValidation.validationMessage)
      .required(`${zipCodeValidation.zipCodeText} is required.`)
      .test('available-zipcode', zipCodeValidation.restrictedValidationMessage, async function (zipcode) {
        if (!(formData.zipCode) && (zipcodeInputValue !== zipcode) && (zipCodeValidation.validationPattern.test(zipcode))) {
          serviceAreaFlag = await PostService.checkAvailableService(zipcode, product, zipSource);
          zipcodeInputValue = zipcode;
          zipCodeValidationStatus = (serviceAreaFlag === true) ? 'valid' : 'invalid';
          if (!serviceAreaFlag) {
            DataLayerService.formFailEvent(form, location, {'zipCode': ['OOA restriction for ' + zipcode + ' failed.']});
          }
          if (userJourneyTracking) {
            DataLayerService.zipCodeEventTracking(formName, subformName, product, zipcodeInputValue, 'completed', zipCodeValidationStatus);
          }
          // Return true if the validation passes, false if it fails
          return serviceAreaFlag;
        }
        else {
          if (formData.zipCode) {
            return true;
          }

          zipCodeValidationStatus = (serviceAreaFlag === true) ? 'valid' : 'invalid';
          if ((userJourneyTracking) && formData.zipCode !== null) {
            DataLayerService.zipCodeEventTracking(formName, subformName, product, zipCode, 'completed', zipCodeValidationStatus);
          }
          return serviceAreaFlag;
        }
      }),
    fullName: yup
      .string()
      .trim()
      .matches(/[^\s\d]+ .+$/, "Full Name must include both First Name and Last Name.")
      .required('Full Name is required.'),
    emailAddress: yup
      .string()
      .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i, 'Email Address is not valid.')
      .required("Email Address is required."),
  });

  const buttonSubmit = (e) => {

    // Validation Error.
    const zipCodevalidation = Validation.ZipValidationCodeDataLayerValidation(zipCode, zipCodeValidation.validationPattern);
    const NameValidation = Validation.FullNameDataLayerValidation(fullName);
    const EmailValidation = Validation.EmailDataLayerValidation(emailAddress);
    const TermsOfServiceValidation = Validation.TermsOfServiceDataLayerValidation(emailOptIn, product);

    // Validation Error.
    const validationError = {
      ...zipCodevalidation,
      ...NameValidation,
      ...EmailValidation,
      ...TermsOfServiceValidation
    };

    if (Object.keys(validationError).length !== 0) {
      DataLayerService.formFailEvent(form, location, validationError);

      modalScrollToTop(props.modalComponent);
    }

    // Scroll to the Top.
    scrollToTop(formRef);
  }

  const handleOnBlur = (event) => {
    switch (event.target.name) {
      case 'zipCode':
        zipCode = event.target.value;
        break;

      case 'emailAddress':
        emailAddress = event.target.value;
        break;

      case 'fullName':
        fullName = event.target.value;
        break;

      default:
        break;
    }

    // Passing lf.form.interaction event to DataLayer.
    DataLayerService.formInteractionEvent(form, location);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const StyledFormContainer = styled(Paper)(({ theme }) => ({
    background: "#ffffff",
    padding: `${theme.spacing(2)} ${isMobile ? theme.spacing(2) : theme.spacing(6)
      }`,
    borderRadius: 8,
    maxWidth: isMobile ? "100%" : 520,
    width: "100%",
    height: isMobile ? "auto" : 720,
  }));

  return (
    <Formik
      initialValues={{
        zipCode: formData.zipCode
          ? formData.zipCode
          : localStorage.getItem("zipCode") || '',
        fullName: '',
        emailAddress: '',
      }}
      validationSchema={ValidationSchema}
      validateOnBlur={true}
      validateOnChange={false}
      onSubmit={async (values) => {
        const cookies = await PostService.getCookieData();
        const { fullName, emailAddress, zipCode } = values;
        const [firstName, lastName] = fullName.trim().split(' ');

        const data = {
          zipCode: zipCode,
          firstName: firstName,
          lastName: lastName,
          emailAddress: emailAddress,
          fullName: fullName,
          utm_campaign: cookies.utm_campaign || '',
          guid: cookies.guid,
        };

        setFormData(data);

        if (userJourneyTracking) {
          let stepNumber = 1;
          if ((componentName === 'ZipChecker') || (componentName === 'ModalForm')) {
            stepNumber = 2;
          }
          // Passing lf.form.status event to DataLayer.
          DataLayerService.formStatusEvent(formName, subformName, product, stepNumber, 3, 'notcompleted');
        }

        // Passing lf.form.assessment event to DataLayer.
        DataLayerService.formAssessmentEvent(data);

        setActiveStepIndex(activeStepIndex + 1);

        // Scroll to the Top.
        scrollToTop(formRef);

        // Passing lf.form.start event to DataLayer.
        DataLayerService.formStartEvent(form, location, data);
      }}
    >
      {({ handleSubmit, setFieldValue, isSubmitting, errors }) => (
        <Container maxWidth={isMobile ? "xs" : "sm"} disableGutters={true}>
          <StyledFormContainer>
            <Form
              className="flex flex-col justify-center items-start step1"
              onSubmit={handleSubmit}
              ref={formRef}
              onBlur={
                (event) => { handleOnBlur(event) }
              }
            >
              {isSubmitting && Object.keys(errors).length > 0 && scrollToTop(formRef)}
              {formData.zipCode ? (
                <div className="area-info p-2 mb-4">
                  <div className="fs-6">
                    We can’t wait for you to join over 1 million satisfied
                    customers!
                  </div>
                </div>
              ) : (
                ""
              )}

              <h3>
                Start your FREE no-obligation estimate &mdash; good for
                one full year!
              </h3>

              <ZipCodeInput
                fieldName={'zipCode'}
                style={{
                  display: formData.zipCode ? "none" : "inline"
                }}
                label= {formData.zipCode ? false : true }
              />
              <Divider className="divider" />

              <TextInput fieldName={'fullName'} label={'Full Name'} placeholder={"E.g., John Smith"} />
              <Divider className="divider" />

              <EmailInput fieldName={'emailAddress'} />
              <Divider className="divider" />

              <LeafBox>
                <LeafButton buttonType={"primary"} type="submit" handleClick={buttonSubmit} style={{
                  width: "100%"
                }} isSubmitting={isSubmitting}>
                  Next
                </LeafButton>
                <ProgressBar customClass="" />
                <TermAndCondition />
              </LeafBox>
            </Form>
          </StyledFormContainer>
        </Container>
      )}
    </Formik>
  );
}

export default Step1;
